import { useInfiniteQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { ContentHeader, QueryStatus, StatusBadge } from "@cx/ui";
import type { ContactStatement } from "@cx/contracts";
import { formatBRL, formatDateBR, type TitleSide } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { useSession } from "../../session";
import { SIDE, titleBadge } from "../finance/labels";
import { STATEMENT_FILTERS, creditLabel } from "./labels";

type Filter = (typeof STATEMENT_FILTERS)[number]["value"];
export type ContactStatementSearch = { side: TitleSide; status: Filter };

export function contactStatementSearch(s: Record<string, unknown>): ContactStatementSearch {
  const side = s.side === "P" ? "P" : "R";
  const status = STATEMENT_FILTERS.some((f) => f.value === s.status) ? (s.status as Filter) : "all";
  return { side, status };
}

const money = (c: number | null) => (c === null ? "—" : formatBRL(c));

/**
 * Ficha financeira do contato (T2.10, RF-FIN-05): contas a receber ou a pagar do cliente/fornecedor com totais em
 * aberto, atrasado e quitado, última baixa e o limite de crédito (lado a receber).
 */
export function ContactStatementPage(): ReactElement {
  const { id } = useParams({ from: "/financas/ficha-financeira/$id" });
  const search = useSearch({ from: "/financas/ficha-financeira/$id" });
  const navigate = useNavigate({ from: "/financas/ficha-financeira/$id" });
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const sides = (["R", "P"] as const).filter((s) => can(perms, SIDE[s].module, "read"));
  const side = sides.includes(search.side) ? search.side : (sides[0] ?? "R");
  const q = useInfiniteQuery({
    queryKey: ["contact-statement", companyId, id, side, search.status],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<ContactStatement>(
        `/contacts/${id}/statement?${new URLSearchParams({
          side,
          status: search.status,
          ...(pageParam ? { cursor: pageParam } : {}),
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const first = q.data?.pages[0];
  const rows = q.data?.pages.flatMap((p) => p.data) ?? [];
  const credit = first ? creditLabel(first.credit) : null;
  const set = (v: Partial<ContactStatementSearch>) => navigate({ search: { ...search, side, ...v } });

  return (
    <>
      <ContentHeader
        title={first ? `Ficha financeira · ${first.contact.name}` : "Ficha financeira"}
        breadcrumb={["Finanças", "Ficha financeira", first?.contact.name ?? ""]}
        actions={
          <Link to="/cadastros/contatos/$id" params={{ id }} className="btn btn-outline-secondary">
            Abrir cadastro
          </Link>
        }
      />
      {sides.length > 1 && (
        <div className="mb-3" style={{ maxWidth: 260 }}>
          <label className="form-label" htmlFor="statement-side">
            Contas
          </label>
          <select
            id="statement-side"
            className="form-select"
            value={side}
            onChange={(e) => set({ side: e.target.value as TitleSide })}
          >
            {sides.map((s) => (
              <option key={s} value={s}>
                {SIDE[s].title}
              </option>
            ))}
          </select>
        </div>
      )}
      <ul className="nav nav-tabs mb-3" role="tablist" aria-label="Situação">
        {STATEMENT_FILTERS.map((f) => (
          <li key={f.value} className="nav-item" role="presentation">
            <button
              type="button"
              role="tab"
              aria-selected={search.status === f.value}
              className={`nav-link${search.status === f.value ? " active" : ""}`}
              onClick={() => set({ status: f.value })}
            >
              {f.label}
            </button>
          </li>
        ))}
      </ul>
      {first && (
        <div className="row g-3 mb-3">
          <div className="col-6 col-md-3">
            <div className="box card p-3 h-100">
              <span className="text-muted small">Em aberto ({first.totals.openCount})</span>
              <span className="fs-5 fw-semibold" data-testid="statement-open">
                {money(first.totals.openCents)}
              </span>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="box card p-3 h-100">
              <span className="text-muted small">Atrasado ({first.totals.overdueCount})</span>
              <span className="fs-5 fw-semibold text-danger">{money(first.totals.overdueCents)}</span>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="box card p-3 h-100">
              <span className="text-muted small">Quitado ({first.totals.paidCount})</span>
              <span className="fs-5 fw-semibold text-success">{money(first.totals.paidCents)}</span>
            </div>
          </div>
          {credit && (
            <div className="col-6 col-md-3">
              <div className="box card p-3 h-100">
                <span className="text-muted small">Crédito</span>
                <span className={`fw-semibold ${first.credit?.exceeded ? "text-danger" : ""}`}>{credit}</span>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="box card">
        <QueryStatus loading={q.isLoading} error={q.error} />
        {q.isSuccess && rows.length === 0 && (
          <p className="text-muted p-3 mb-0">Nenhuma conta neste filtro.</p>
        )}
        {rows.length > 0 && (
          <div className="table-responsive">
            <table className="table align-middle mb-0" aria-label="Contas do contato">
              <thead>
                <tr>
                  <th scope="col">Vencimento</th>
                  <th scope="col">Descrição</th>
                  <th scope="col">Emissão</th>
                  <th scope="col" className="text-end">
                    Valor
                  </th>
                  <th scope="col" className="text-end">
                    Pago
                  </th>
                  <th scope="col" className="text-end">
                    Em aberto
                  </th>
                  <th scope="col">Situação</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const b = titleBadge(r);
                  return (
                    <tr key={r.id}>
                      <td>
                        <Link to={SIDE[r.side].detail} params={{ id: r.id }}>
                          {formatDateBR(r.dueDate)}
                        </Link>
                      </td>
                      <td>
                        {r.description ?? "—"}
                        {r.documentNumber && (
                          <span className="text-muted small"> · doc. {r.documentNumber}</span>
                        )}
                      </td>
                      <td>{formatDateBR(r.issueDate)}</td>
                      <td className="text-end">{formatBRL(r.amountCents)}</td>
                      <td className="text-end">
                        {formatBRL(r.paidCents)}
                        {r.lastPaidAt && (
                          <div className="small text-muted">em {formatDateBR(r.lastPaidAt)}</div>
                        )}
                      </td>
                      <td className="text-end">{formatBRL(r.openCents)}</td>
                      <td>
                        <StatusBadge tone={b.tone}>{b.label}</StatusBadge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {q.hasNextPage && (
          <div className="p-3 text-center">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={q.isFetchingNextPage}
              onClick={() => q.fetchNextPage()}
            >
              Carregar mais
            </button>
          </div>
        )}
      </div>
    </>
  );
}
